<template>
  <div class="manual p-3">
    <header>
      <h3 class="title text-secondary">
        User Manual
        <hr />
      </h3>
    </header>
    <main class="p-3">
      <section>
        <p>
          ED<sup>2</sup> has an intuitive interface, which allows you to evaluate the pros and cons of each alternative in order to help you select the best one.
        </p>
      </section>
      <section>
        <p>
          ED<sup>2</sup> provides you with 2 modes:  decision-making and problem-solving
          <br />
          <br />
          <strong>Decision-making</strong> is a compact mode, which is quick and less accurate<br />
          • helps make quick decisions on the fly, but these decisions have a low level of instrumental rationality (accuracy)<br />
          • the decision is problem-centered & targeted to reach the proximate (short-term) goal<br />
          <br />
          <strong>Problem-solving</strong> is an expanded mode, which is less quick but more accurate<br />
          • requires setting goals & recognizing problems<br />
          • arriving at the decision (solution) is not as rapid as in decision-making, but its level of instrumental rationality (accuracy) is higher because it’s supported in applying the principle of instrumental rationality<br />
          • the decision is user-centered & targeted to reach the proximate goal from the perspective of the distal (long-term) goal<br />
        </p>
        <p>For each of these options, you can SAVE your intermediate and/or final work results and SHARE decision trees with other ED<sup>2</sup> users.</p>
        <p>To make a quick decision, it is recommended that you first begin by using the decision-making option of ED<sup>2</sup>, and only in the case that this option does not provide a suitable decision, to begin working with the problem-solving option.</p>
        <p>The diagram for decision-making and problem-solving using ED<sup>2</sup> is presented below.</p>
        <!--<img :src="`${publicPath}/img/flow.png`">-->
        <div>
          <b-img :src="decisionMakingFlow" fluid alt="Responsive image"></b-img>
        </div>
        <br />
      </section>
      <section>
        <ol class="card-text">
          <li>
            <strong>
              Application of ED<sup>2</sup> for decision-making
            </strong>
            <section>
              <p>
                <br />
                <ol>
                  <li>
                    User names the “Problem.”
                  </li>
                  <li>
                    User measures pros and cons of the alternatives’ outcomes by using the <strong>IL-Frame</strong> (see 3. IL-Frame for measuring pros and cons of outcomes). If this evaluation is difficult, user generates hypotheses and sub-hypotheses for the evaluation.
</li>
                  <li>
                    User taps <em>Help me to decide</em> to allow ED<sup>2</sup> to determine the best alternative.
</li>
                  <li>
                    If the best alternative has a level of motivation (preference) ≥ 51% and there are no other alternatives close to this alternative, it means that the user is motivated to select this alternative, which produces the most suitable course of action.
                  </li>
                  <li>
                    If the best alternative has a level of motivation (preference) ≥ 51% and this level is close (difference ≤ 5%) to some other alternative(s), the user will receive a <em>proximity alert</em>. This means the user has difficulties distinguishing among the alternatives and needs to apply (if time allows) the problem-solving mode of ED<sup>2</sup> to resolve their difficulties. This may help the user differentiate among the alternatives to find the most suitable course of action.
</li>
                  <li>
                    If the best alternative has a level of motivation (preference) ≤ 50%, the user will receive a <em>low motivation alert</em>. This means the user is not motivated to select any of the considered alternatives and must add a new alternative to measure its pros and cons. If a new alternative is not available and the user has enough time, they need to apply the problem-solving option of ED<sup>2</sup> to find the most suitable course of action.
</li>
                </ol>
              </p>
            </section>
          </li>
          <li>
            <strong>
              Application of ED<sup>2</sup> for Problem-Solving section
            </strong>
            <section>
              <p>
                <br />
                <ol>
                  <li>
                    User names the “Problem,” “Proximate goal,” and “Distal goal.”
                    <br />
                    The proximate goal is a short-term goal. The distal goal is a long-term goal.
                    <br />
                    Note: any changes in these goals will change the problem.
                  </li>
                  <li>
                    For each alternative, ED<sup>2</sup> automatically generates two options: for successful outcomes (option 1) and for unsuccessful outcomes (option 2) from the perspective of the proximate goal.
                    <br />
                    •	Option 1: <strong>helps “Proximate goal,”</strong>
                    when the alternative helps reach the proximate goal
                    <br />
                    •	Option 2: <strong>doesn’t help “Proximate goal,”</strong> when the alternative doesn’t help reach the proximate goal
                  </li>
                  <li>
                    User measures (from the perspective of “Distal goal”) the pros and cons of the alternatives’ outcomes for both successful and unsuccessful options by using the <strong>IL-Frame</strong> (see 3. IL-Frame for measuring pros and cons of outcomes). This allows ED<sup>2</sup> to help the user implement the principle of instrumental rationality in reaching the proximate goal from the perspective of the distal goal. If this evaluation is difficult, the user generates hypotheses and sub-hypotheses for evaluation in the same way as for the decision-making option of ED<sup>2</sup>.
</li>
                  <li>
                    User taps <em>Help me to decide</em> to allow ED<sup>2</sup> to determine the best alternative.
</li>
                  <li>
                    If the best alternative has a level of motivation (preference) ≥ 51% and there are no other alternatives close to this alternative, it means that the user is motivated to choose this alternative, which produces the most suitable course of action.
                  </li>
                  <li>
                    If the best alternative has a level of motivation (preference) ≥ 51% and this level is close (difference ≤ 5%) to some other alternative(s), the user will receive a <em>proximity alert</em>. This means that the user has difficulties distinguishing among the alternatives with the existing distal goal. In this situation, the distal goal as a long-term goal should be reset (renamed) and made more long-term. If the user has enough time to continue searching for the most suitable course of action, they will reevaluate each alternative from the perspective of the new distal goal and repeat this process until the most suitable course of action is determined.
                    <br />
                    Note: making the distal goal even more distal helps the user differentiate the alternatives to find the most suitable course of action.
                  </li>
                  <li>
                    If the best alternative has a level of motivation (preference) ≤ 50%, the user will receive a <em>low motivation</em> alert. This means the user is not motivated to select any of the considered alternatives and must add a new alternative to measure its pros and cons from the perspective of the distal goal. If the new alternative is not available, but the user has enough time to continue searching for the most suitable course of action, they need to reevaluate each alternative from the perspective of a new distal goal, which should be more long-term than the previous one.
</li>
                </ol>
              </p>
            </section>
          <li>
            <strong>
              IL-Frame for measuring pros and cons of outcomes
            </strong>
            <section>
              <p>
                <br />
                IL-Frame allows the user to measure the following four verbal characteristics to determine pros and cons of the alternatives’ outcomes:<br /><br />
                •	<em>intensity</em> (I+) and <em>likelihood</em> (L+) of <em>positive outcomes</em> that determine their pros<br />
                •	<em>intensity</em> (I–) and <em>likelihood</em> (L–) of <em>negative outcomes</em> that determine their cons<br /><br />
                <ol>
                  <li>
                    <strong>Scales for measuring</strong><br />
                    •	<em>Intensity scale</em>: extremely weak, very weak, weak, not weak, not strong, strong, very strong, extremely strong<br />
                    •	<em>Likelihood scale</em>: extremely seldom, very seldom, seldom, not seldom, not often, often, very often, extremely often<br /><br />
                  </li>
                  <li>
                    <strong>Instructions for measuring intensity and likelihood</strong><br />
                    1)	When you are measuring the intensity of positive outcomes on the intensity scale, you need to consider how appealing positive outcomes are to you.<br />
                    2)	When you are measuring the likelihood of positive outcomes on the likelihood scale, you need to consider how likely you are to overcome the difficulty of obtaining positive outcomes.<br />
                    3)	When you are measuring the intensity of negative outcomes on the intensity scale, you need to consider how unappealing negative outcomes are to you.<br />
                    4)	When you are measuring the likelihood of negative outcomes on the likelihood scale, you need to consider how likely you are to overcome the difficulty of avoiding negative outcomes.<br /><br />
                  </li>
                  <li>
                    <strong>Levels of motivation</strong><br />
                    ED<sup>2</sup> uses intensity and likelihood of outcomes to determine the levels of positive (M+) and negative (M-) motivation for choosing an alternative:<br />
                    •	M+ (I+, L+) – the <em>level of positive motivation</em> or <em>pros</em> (as a function of positive intensity and positive likelihood)<br />
                    •	M- (I-, L-) – the <em>level of negative motivation</em> or <em>cons</em> (as a function of negative intensity and negative likelihood)<br />
                    <br />
                    For example,<br />
                    • if you select “very strong” for positive intensity and “not seldom, not often” for positive likelihood, this means that you not seldom, not often expect very strong positive outcomes, and your pros is 68% (<span style="color:green;"><em>M+ = 68%</em></span>)<br />
                    •	if you select “strong” for negative intensity and “often” for negative likelihood, this means that you often expect strong negative outcomes, and your cons is 64% (<span style="color:red;"><em>M- = 64%</em></span>)<br />
                    <br />
                    ED<sup>2</sup> uses pros and cons to determine the overall level of motivation (M) for selecting an alternative:<br />
                    • M (M+, M-) – the level of motivation (as a function of pros and cons) <br /><br />
                    For the above example, ED<sup>2</sup> will determine the level of motivation at 53% (M = 53%) by showing this on the screen as <em>53% (<span style="color:green;">+68%</span>, <span style="color:red;">-64%</span>)</em>:<br />
                    <br /><div>
                      <b-img :src="levelOfMotivation" fluid alt="Responsive image"></b-img>
                    </div>
                    <br />
                  </li>
                </ol>
              </p>
            </section>
          </li>
          <li>
            <strong>
              Considering hypotheses
            </strong>
  <section>
    <p>
      <br />
      If you are experiencing difficulty measuring intensity or likelihood, this means that the problem of evaluating the alternative is difficult for you, so you need to split this problem into sub-problems by considering hypothetical situations (hypotheses). You need to consider two exclusive hypothetical situations that could help you evaluate the alternative. If you’re still uncertain about measuring intensity or likelihood for this sub-problem, you must continue the splitting process until you arrive at a situation with outcomes that can be measured.    </p>
  </section>
</li>
        </ol>
      </section>
    </main>
  </div>
</template>

<script>

  import decisionMakingFlow from "./../../assets/flow.png"
  import levelOfMotivation from "./../../assets/lom.png"
  export default {
    data: function () {
        return {
            decisionMakingFlow: decisionMakingFlow,
            levelOfMotivation: levelOfMotivation
        }
    }
  }
</script>
<style lang = "scss" >
  li > section > p > ol > li {
  list-style: lower-roman;
}
  .manual {
    display: flex;
    flex-direction: column;
    height: calc(100% - 56px);
    .title {
      font-weight: bold;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      hr {
        width: 50%;
        max-width: 200px;
        margin: 0.5em 0;
        border-color: #888;
      }
    }
    main {
      flex-grow: 1;
      overflow-y: auto;
      p {
        text-align: justify;
        .underline {
          text-decoration: underline;
        }
      }
    }
  }
</style>
